// Solar calculation utilities for client-side estimates

export const CONSTANTS = {
  ACRES_TO_SQM: 4046.86,
  PANEL_COVERAGE: 0.5,
  PEAK_IRRADIANCE: 1000, // W/m2
  AVG_HOME_DAILY_KWH: 10,
  UK_GRID_CARBON_INTENSITY: 200, // gCO2/kWh
};

export const PANEL_EFFICIENCIES = {
  high: { value: 0.15, label: 'High-end (15%)' },
  average: { value: 0.12, label: 'Average (12%)' },
  old: { value: 0.10, label: 'Older panels (10%)' },
};

export interface QuickEstimate {
  peakPowerMW: number;
  optimalDayMWh: number;
  averageDayMWh: number;
  homesPowered: number;
}

export function calculateQuickEstimate(
  areaAcres: number,
  efficiency: keyof typeof PANEL_EFFICIENCIES
): QuickEstimate {
  const effValue = PANEL_EFFICIENCIES[efficiency].value;
  const areaSqm = areaAcres * CONSTANTS.ACRES_TO_SQM;
  const panelArea = areaSqm * CONSTANTS.PANEL_COVERAGE;
  
  // Peak power in MW
  const peakPowerMW = (panelArea * CONSTANTS.PEAK_IRRADIANCE * effValue) / 1_000_000;
  
  // Daily energy estimates
  const optimalDayMWh = peakPowerMW * 6 * 0.85; // 6 peak hours, 85% efficiency
  const averageDayMWh = peakPowerMW * 3.5 * 0.75; // 3.5 peak hours UK average
  
  // Homes powered (based on average day)
  const homesPowered = Math.floor((averageDayMWh * 1000) / CONSTANTS.AVG_HOME_DAILY_KWH);
  
  return {
    peakPowerMW: Math.round(peakPowerMW * 10) / 10,
    optimalDayMWh: Math.round(optimalDayMWh * 10) / 10,
    averageDayMWh: Math.round(averageDayMWh * 10) / 10,
    homesPowered,
  };
}

export function formatPower(mw: number): string {
  if (mw >= 1000) {
    return `${(mw / 1000).toFixed(1)} GW`;
  }
  return `${mw.toFixed(1)} MW`;
}

export function formatEnergy(mwh: number): string {
  if (mwh >= 1000) {
    return `${(mwh / 1000).toFixed(1)} GWh`;
  }
  return `${mwh.toFixed(1)} MWh`;
}

export function formatNumber(num: number): string {
  return num.toLocaleString();
}

